import { faDiscord, faInstagram, faTiktok, faXTwitter, faYoutube } from '@fortawesome/free-brands-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ReactNode, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, NavLink } from 'react-router-dom'
import urls from '../external_urls'
import { useAuth } from './Auth'
import bravoReadyLogo from '../../assets/bravo-ready-logo.png'
import { Dialog } from './dialog'
import { Terms } from './terms'
import img from '~/src/assets/br1_logo.webp'

/**
 * One column of the footer's link grid.
 *
 * The three columns each built their own heading, and the heading carried `min-h-[3em]` to force
 * them level — which only worked while every heading was one line. On a phone the grid was
 * `grid-cols-2` with three children, so the third column dropped onto a row of its own at half
 * width, "JOIN OUR COMMUNITY" wrapped inside it, and its 3em box then pushed the social buttons
 * out of line with everything above them.
 *
 * A shared component instead, with the community column spanning the full width on mobile — where
 * it has room for its heading and its five buttons — and the reserved height gone, because a
 * two-line heading is now something the layout survives rather than something it hides.
 */
const FooterColumn = ({
    title,
    className = '',
    children,
}: {
    title: string
    className?: string
    children: ReactNode
}) => (
    <div className={`flex flex-col items-center gap-y-2 ${className}`}>
        {/*
          No rule under the heading. There was a `h-px w-8` gradient one, and it rendered as a fat
          coloured slab: the grid below carries `[&_span]:py-1.5` for the Terms trigger, that
          selector caught this span too, and a background gradient paints the padding box — so a
          1px rule came out 13px tall. The lesson is the blanket selector, which is why the grid now
          targets links only and Terms carries its own padding.

          It is not coming back with the padding fixed. Three coloured ticks under three headings is
          decoration the footer was not asking for; spacing and letter-spacing separate the heading
          from its links perfectly well.
        */}
        <header className='mb-2 text-sm font-black uppercase tracking-[0.2em] text-white'>{title}</header>
        {children}
    </div>
)

const socials = [
    { label: 'Discord', href: urls.discord, icon: faDiscord },
    { label: 'X', href: urls.twitter, icon: faXTwitter },
    { label: 'Instagram', href: urls.instagram, icon: faInstagram },
    { label: 'TikTok', href: urls.tiktok, icon: faTiktok },
    { label: 'YouTube', href: urls.youtube, icon: faYoutube },
]

export const PageFooter = () => {
    const { t } = useTranslation()
    const { auth } = useAuth()

    const ref = useRef(null)

    return (
        <div
            ref={ref}
            className='flex flex-col items-center uppercase text-xs text-white/60 text-center max-w-[1920px] mx-auto py-8'
        >
            <img src={img} className='w-2/5 max-w-xs mb-8' />
            {/*
              Footer links are xs text stacked on a 2-unit gap, which gives a thumb about 16px to
              aim at. Padding every link in one place beats touching each of them.

              `items-start` so a column that runs longer than its neighbour stretches nothing but
              itself, and `max-w-5xl` so the three columns stay a group rather than drifting to
              opposite ends of a 1920px page.
            */}
            {/* `[&_a]` only. It used to say `[&_a]:py-1.5 [&_span]:py-1.5`, and "every span in the
                footer" is a net wide enough to catch anything a column ever puts inside itself. */}
            <div className='grid w-4/5 max-w-5xl grid-cols-2 items-start gap-x-6 gap-y-10 font-black md:grid-cols-3 [&_a]:py-1.5'>
                {/* Trailers and Game mechanics are deliberately absent. Both jumped back to
                    sections of the landing page the visitor has already scrolled past to reach
                    this footer, so they sent people backwards; the footer is for the things that
                    are not on the page. */}
                <FooterColumn title={t('footer.about.header')}>
                    <Link to={{ pathname: '/', hash: 'partners' }} className='hover:text-white'>
                        {t('footer.about.partners_and_investors')}
                    </Link>
                    <Link to={{ pathname: '/', hash: 'news' }} className='hover:text-white'>
                        {t('footer.about.news')}
                    </Link>
                    <Dialog>
                        <Dialog.Content>
                            <Terms />
                        </Dialog.Content>
                        <Dialog.Trigger>
                            {/* Its own padding now the grid no longer hands it out to every span. */}
                            <span className='block cursor-pointer py-1.5 hover:text-white'>Terms Of Use</span>
                        </Dialog.Trigger>
                    </Dialog>
                </FooterColumn>

                <FooterColumn title='DOWNLOAD'>
                    {auth?.is_auth == false && (
                        <NavLink to='/signup' className='hover:text-white uppercase'>
                            {t('footer.sign_up')}
                        </NavLink>
                    )}
                    <NavLink to='/download' className='hover:text-white'>
                        DOWNLOAD BR1
                    </NavLink>
                    <a href={urls.media_kit} target='_blank' className='hover:text-white'>
                        MEDIA KIT
                    </a>
                    {/* Next to the media kit deliberately: both are what a creator comes here for. */}
                    <NavLink to='/affiliate' className='hover:text-white'>
                        AFFILIATE PROGRAM
                    </NavLink>
                </FooterColumn>

                {/* Full width below `md`. Five 44px buttons need 236px with their gaps, which is
                    more than half of a 375px screen minus the grid gutter — so in a half-width cell
                    they wrapped to two rows under a two-line heading. */}
                <FooterColumn title={t('footer.social.header')} className='col-span-2 md:col-span-1'>
                    {/* Stacked text links gave a 16px tap target and read as fine print next to
                        the rest of the footer. Square bordered buttons match the icon row the
                        emails already use, and are thumb-sized. */}
                    <div className='flex flex-row flex-wrap items-center justify-center gap-2'>
                        {socials.map(({ label, href, icon }) => (
                            <a
                                key={label}
                                href={href}
                                target='_blank'
                                rel='noreferrer'
                                aria-label={label}
                                title={label}
                                className='flex h-11 w-11 items-center justify-center border border-white/25 text-base transition-colors hover:border-white/60 hover:bg-white/5 hover:text-white'
                            >
                                <FontAwesomeIcon icon={icon} />
                            </a>
                        ))}
                    </div>
                </FooterColumn>
            </div>
            {/*
              Left to right on a desktop, but the stack order is not the same list read downwards.
              On a phone this was the copyright line, then the beta badge, then the studio — which
              opens the footer's last block on its least interesting line and ends it on a logo with
              nothing under it. Reversed with `order`, the two things worth seeing come first and the
              legal line closes the page, which is where a legal line goes.
            */}
            <div className='w-full bg-[#131313] mt-10 text-white border-t border-white/10'>
                <div className='grid grid-cols-1 md:grid-cols-3 items-center justify-center w-4/5 mx-auto py-8 font-black gap-y-6'>
                    <div className='order-3 flex flex-col items-center justify-center gap-2 text-xs text-white/60 md:order-none md:text-white'>
                        <div>© {new Date().getFullYear()} BR1: INFINITE</div>
                    </div>
                    <div className='order-1 w-max mx-auto border border-white/40 rounded-full py-2 px-8 text-xs tracking-[0.2em] md:order-none'>
                        OPEN BETA PHASE
                    </div>
                    {/* `max-w-full`, not `w-max`. Sizing the anchor to the logo's intrinsic 454px
                        gave Tailwind's `img { max-width: 100% }` a 454px box to resolve against, so
                        the image never shrank and the whole page scrolled sideways on a phone. */}
                    <a
                        href='https://bravoready.com'
                        target='_blank'
                        rel='noreferrer'
                        aria-label='Bravo Ready'
                        className='order-2 mx-auto block max-w-full transition-opacity hover:opacity-80 md:order-none'
                    >
                        <img
                            src={bravoReadyLogo}
                            alt='Bravo Ready'
                            width={454}
                            height={97}
                            className='mx-auto h-auto w-auto max-w-[13rem] md:max-w-full'
                        />
                    </a>
                </div>
            </div>
        </div>
    )
}
